'use client';

import { correctDateTime } from '@/components/girl/request-date';
import { cn } from '@bells/core/lib/utils';
import {
  attendantFeeTitle,
  attendantThreesomeFFM,
  attendantThreesomeGangbang,
  attendantThreesomeMMF,
  attendantThreesomeSwinger,
  attendantTravelCostsTitle,
  contactFormSubject,
  contactFormSuccess,
  requestConfirmDescription,
  requestConfirmTitle,
  requestDetailsDisclaimerYes,
  requestDetailsDisclaimerYesNotice,
  requestDetailsDisclaimerYesNoticeTitle,
  requestDetailsNoticeDescription,
  requestDetailsNoticePlaceholder,
  requestDetailsNoticeTitle,
  requestDuoDateFeeTitle,
  requestDuoDateTravelCostsTitle,
  requestPriceOnRequest,
  requestSubmit,
  requestTotalPrice,
  wizardPrev,
} from '@bells/i18n';
import {
  AttendantSearchSingleResponse,
  ThreesomeStatus,
} from '@bells/schema/models/attendant';
import { RequestLocationType } from '@bells/schema/models/mapbox';
import { Button } from '@bells/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@bells/ui/components/form';
import React, { useMemo, useState } from 'react';

import { RequestContext } from '@/components/girl/request-context';
import {
  calculatePrice,
  formatDateIntlLong,
  formatDurationPlane,
  moneyFormatter,
} from '@/components/girl/utils';
import { useLanguage } from '@bells/core/i18n';
import { generateRestClient } from '@bells/core/rest/react';
import {
  RequestContactType,
  RequestFormSchema,
} from '@bells/schema/models/request';
import { Checkbox } from '@bells/ui/components/checkbox';
import { Separator } from '@bells/ui/components/separator';
import { useValidationForm } from '@bells/ui/hooks/use-validation';

import attendantFrontend from '@bells/api/attendants/frontend';

import { StepChooseContactDetails } from '@/components/girl/step-choose-contact-details';
import { StepChooseDate } from '@/components/girl/step-choose-date';
import { StepChooseLocationType } from '@/components/girl/step-choose-location-type';
import { StepChooseService } from '@/components/girl/step-choose-services';
import { StepChooseAddress } from '@/components/girl/step-chopse-address';
import requestFrontend from '@bells/api/request/frontend';
import { baseLocale } from '@bells/i18n/runtime';
import { AutosizeTextarea } from '@bells/ui/components/autosize-textarea';
import {
  FormRoot,
  FormServerError,
  SubmitButton,
} from '@bells/ui/components/form-root';
import { toast } from '@bells/ui/utils/use-toast';
import { isEmpty } from 'lodash';
import { StepChooseDuoPartner } from '@/components/girl/step-choose-duo-partner';
import { TooltipPortal, TooltipProvider } from '@bells/ui/components/tooltip';
const systemRestClient = generateRestClient(attendantFrontend);

export default function RequestModal({
  data,
  open,
  setOpen,
}: {
  data: AttendantSearchSingleResponse;
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const { language } = useLanguage();
  const [step, setStep] = useState<number>(0);
  const form = useValidationForm({
    query: {
      locale: language,
    },
    schema: RequestFormSchema,
    router: requestFrontend.doRequest,
    pathParams: { slug: data.slug },
    mode: 'onChange',
    defaultValues: {
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      firstName: '',
      lastName: '',
      email: '',
      addressAdditional: '',
      duration: undefined,
      startDate: [correctDateTime(new Date())],
      contactMethod: RequestContactType.EMAIL,
      location: RequestLocationType.HOTEL,
      service: undefined,
    },
    onSuccess: () => {
      setStep(0);
      form.reset();
      setOpen(false);
      toast({
        title: contactFormSubject(),
        description: contactFormSuccess(),
      });
    },
  });

  const duration = form.watch('duration');
  const address = form.watch('address');
  const threesomeStatus = form.watch('service');
  const formValues = form.watch();
  const duoDatePartnerWatch = form.watch('duoDatePartner');

  const totalPrice = useMemo(() => {
    return calculatePrice(duration, {
      prices: data.prices ?? null,
      perExtraDay: data.perExtraDay,
      ffmPrice: data.ffmPrice,
      mmfPrice: data.mmfPrice,
      swingerPrice: data.swingerPrice,
      gangbangPrice: data.gangbangPrice,
    });
  }, [duration]);

  const threesome = useMemo<
    | {
        price: number | undefined;
        label: string;
      }
    | undefined
  >(() => {
    if (!totalPrice || !threesomeStatus) {
      return undefined;
    }

    if (threesomeStatus == ThreesomeStatus.MMF) {
      return {
        price: data.mmfPrice ? totalPrice * (data.mmfPrice / 100) : undefined,
        label: attendantThreesomeMMF(language),
      };
    } else if (threesomeStatus == ThreesomeStatus.FFM) {
      return {
        price: data.ffmPrice ? totalPrice * (data.ffmPrice / 100) : undefined,
        label: attendantThreesomeFFM(language),
      };
    } else if (threesomeStatus == ThreesomeStatus.SWINGER) {
      return {
        price: data.swingerPrice
          ? totalPrice * (data.swingerPrice / 100)
          : undefined,
        label: attendantThreesomeSwinger(language),
      };
    } else if (threesomeStatus == ThreesomeStatus.GANGBANG) {
      return {
        price: data.gangbangPrice
          ? totalPrice * (data.gangbangPrice / 100)
          : undefined,
        label: attendantThreesomeGangbang(language),
      };
    }

    return undefined;
  }, [totalPrice, threesomeStatus]);

  const { data: travelDistanceData, isLoading: isLoadingCalculate } =
    systemRestClient.travelCosts.useQuery({
      queryKey: ['travel-costs', address?.lat, address?.lng, data.slug],
      queryData: {
        query: { lat: address?.lat ?? 0, lng: address?.lng ?? 0 },
        params: { slug: data.slug },
      },
      enabled: address?.lat && address?.lng ? true : false,
      select: (df) => df.body,
    });

  const { data: duoDatePartnerTravelDistanceData } =
    systemRestClient.travelCosts.useQuery({
      queryKey: [
        'travel-costs',
        address?.lat,
        address?.lng,
        duoDatePartnerWatch,
      ],
      queryData: {
        query: { lat: address?.lat ?? 0, lng: address?.lng ?? 0 },
        params: { slug: duoDatePartnerWatch ?? '' },
      },
      enabled:
        address?.lat && address?.lng && duoDatePartnerWatch ? true : false,
      select: (df) => df.body,
    });
  const { data: dataDurations } = systemRestClient.durations.useQuery({
    queryKey: ['durations', address?.lat, address?.lng, data.slug],
    queryData: {
      query: { lat: address?.lat ?? 0, lng: address?.lng ?? 0 },
      params: { slug: data.slug ?? '' },
    },
    enabled: address?.lat && address?.lng ? true : false,
    select: (df) => df.body,
  });

  const { data: duoDatePartnerData } = systemRestClient.bySlug.useQuery({
    queryKey: ['duoDate', duoDatePartnerWatch],
    queryData: {
      params: { slug: duoDatePartnerWatch ?? '' },
    },
    enabled: duoDatePartnerWatch ? true : false,
    select: (df) => df.body,
  });

  const duoDatePartnerPrice = useMemo(() => {
    if (!duoDatePartnerData || !duration) {
      return undefined;
    }

    return calculatePrice(duration, {
      prices: duoDatePartnerData.prices ?? null,
      perExtraDay: duoDatePartnerData.perExtraDay,
      ffmPrice: duoDatePartnerData.ffmPrice,
      mmfPrice: duoDatePartnerData.mmfPrice,
      swingerPrice: duoDatePartnerData.swingerPrice,
      gangbangPrice: duoDatePartnerData.gangbangPrice,
    });
  }, [duoDatePartnerData, duration]);
  const close = () => {
    setOpen(false);
    form.reset();
    setStep(0);
  };
  return (
    <TooltipProvider>
      <RequestContext.Provider
        value={{
          dataDuration: dataDurations ?? [],
          totalPrice: totalPrice,
          isLoadingCalculate,
          travelCosts: travelDistanceData?.costs,
          distance: travelDistanceData?.distance,
          values: form.getValues(),
          service: threesomeStatus ?? undefined,
          setValues: (val) => {
            form.reset(
              { ...form.getValues(), ...val },
              { keepErrors: true, keepIsValid: false, keepIsValidating: false }
            );
          },
        }}
      >
        <Dialog
          open={open}
          modal={true}
          onOpenChange={(val) => {
            if (val === false) {
              close();
            }
            setOpen(val);
          }}
        >
          <DialogContent className='flex h-full max-h-screen flex-col justify-between gap-0 overflow-y-auto p-0 lg:h-auto lg:max-w-[700px]'>
            <div
              className={cn('flex h-full w-full flex-col', {
                hidden: step != 0,
              })}
            >
              <StepChooseLocationType
                resolve={() => setStep(1)}
                data={data}
              ></StepChooseLocationType>
            </div>
            <div
              className={cn('flex h-full w-full flex-col', {
                hidden: step != 1,
              })}
            >
              <StepChooseAddress
                goBack={() => setStep(0)}
                resolve={() => setStep(2)}
                data={data}
              ></StepChooseAddress>
            </div>

            <div
              className={cn('flex h-full w-full flex-col', {
                hidden: step != 2,
              })}
            >
              <StepChooseDate
                goBack={() => setStep(1)}
                resolve={() => setStep(3)}
                data={data}
              ></StepChooseDate>
            </div>

            <div
              className={cn('flex h-full w-full flex-col', {
                hidden: step != 3,
              })}
            >
              <StepChooseService
                goBack={() => setStep(2)}
                resolve={(service) => {
                  if (
                    service &&
                    [
                      ThreesomeStatus.ESCORT_PARTNER_BISEXUAL,
                      ThreesomeStatus.ESCORT_PARTNER_HETERO,
                    ].includes(service)
                  ) {
                    setStep(4);
                  } else {
                    setStep(5);
                  }
                }}
                data={data}
                address={address}
                duration={duration}
              ></StepChooseService>
            </div>

            {threesomeStatus &&
              [
                ThreesomeStatus.ESCORT_PARTNER_BISEXUAL,
                ThreesomeStatus.ESCORT_PARTNER_HETERO,
              ].includes(threesomeStatus) && (
                <div
                  className={cn('flex h-full w-full flex-col', {
                    hidden: step != 4,
                  })}
                >
                  <StepChooseDuoPartner
                    goBack={() => setStep(3)}
                    resolve={() => setStep(5)}
                    data={data}
                    address={address}
                    duration={duration}
                  ></StepChooseDuoPartner>
                </div>
              )}

            <div
              className={cn('flex h-full w-full flex-col', {
                hidden: step != 5,
              })}
            >
              <StepChooseContactDetails
                goBack={() => {
                  if (
                    threesomeStatus &&
                    [
                      ThreesomeStatus.ESCORT_PARTNER_BISEXUAL,
                      ThreesomeStatus.ESCORT_PARTNER_HETERO,
                    ].includes(threesomeStatus)
                  ) {
                    setStep(4);
                  } else {
                    setStep(3);
                  }
                }}
                resolve={() => setStep(6)}
              ></StepChooseContactDetails>
            </div>

            <FormRoot
              form={form}
              className={cn('flex h-full w-full flex-col', {
                hidden: step != 6,
              })}
            >
              <DialogHeader className='p-6 pb-0'>
                <DialogTitle>{requestConfirmTitle()}</DialogTitle>
                <DialogDescription>
                  {requestConfirmDescription()}
                </DialogDescription>
              </DialogHeader>
              <FormServerError></FormServerError>
              <div className='flex-1 gap-4 overflow-y-auto p-6'>
                <div className='grid w-full grid-cols-2 gap-4'>
                  <span className='text-muted-foreground'>Dauer des Dates</span>
                  <span className='text-end font-medium'>
                    {formatDurationPlane(formValues.duration, language)}
                  </span>
                  {address && (
                    <>
                      <span className='text-muted-foreground'>Addresse</span>
                      <div className='flex flex-col text-end font-medium'>
                        <span>{address.name}</span>
                        <span className='text-xs font-normal text-muted-foreground'>
                          {address.address}
                        </span>
                      </div>
                    </>
                  )}

                  <Separator className='col-span-2 w-full'></Separator>
                  <span className='text-muted-foreground'>Termine</span>
                  <div className='flex flex-col justify-end gap-1 text-end'>
                    {formValues.startDate.map((df, index) => {
                      return (
                        <React.Fragment key={`date-${index}}`}>
                          {index > 0 && (
                            <span className='text-xs text-muted-foreground'>
                              oder
                            </span>
                          )}
                          <span className='text-end font-medium'>
                            {formatDateIntlLong(df)}
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>

                  <Separator className='col-span-2 w-full'></Separator>
                  <span className='col-span-2 w-full font-bold'>
                    {data.name}
                  </span>

                  <span className='text-muted-foreground'>
                    {requestDuoDateFeeTitle({ name: data.name })}
                  </span>
                  <span className='text-end font-medium'>
                    {totalPrice !== undefined
                      ? moneyFormatter.format(totalPrice)
                      : requestPriceOnRequest()}
                  </span>

                  <span className='text-muted-foreground'>
                    {requestDuoDateTravelCostsTitle({ name: data.name })}
                  </span>
                  <div className='flex flex-col text-end font-medium'>
                    {travelDistanceData?.costs !== undefined &&
                    (formValues.location ==
                      RequestLocationType.PRIVATE_ADDRESS ||
                      formValues.location == RequestLocationType.HOTEL) ? (
                      <>
                        <span>
                          {moneyFormatter.format(travelDistanceData.costs)}
                        </span>
                        <span className='text-xs font-normal text-muted-foreground'>
                          {`${travelDistanceData.distance} km`}
                        </span>
                      </>
                    ) : (
                      requestPriceOnRequest()
                    )}
                  </div>

                  {threesome !== undefined && (
                    <>
                      <span className='text-muted-foreground'>
                        {threesome.label}
                      </span>
                      <div className='flex flex-col text-end font-medium'>
                        {threesome.price !== undefined ? (
                          <span>{moneyFormatter.format(threesome.price)}</span>
                        ) : (
                          requestPriceOnRequest()
                        )}
                      </div>
                    </>
                  )}

                  {duoDatePartnerWatch && (
                    <>
                      <span className='col-span-2 w-full font-bold'>
                        {duoDatePartnerData?.name ?? ''}
                      </span>

                      <span className='text-muted-foreground'>
                        {requestDuoDateFeeTitle({
                          name: duoDatePartnerData?.name ?? '',
                        })}
                      </span>
                      <span className='text-end font-medium'>
                        {duoDatePartnerPrice !== undefined
                          ? moneyFormatter.format(duoDatePartnerPrice)
                          : requestPriceOnRequest()}
                      </span>

                      <span className='text-muted-foreground'>
                        {requestDuoDateTravelCostsTitle({
                          name: duoDatePartnerData?.name ?? '',
                        })}
                      </span>
                      <div className='flex flex-col text-end font-medium'>
                        {duoDatePartnerTravelDistanceData?.costs !==
                          undefined &&
                        (formValues.location ==
                          RequestLocationType.PRIVATE_ADDRESS ||
                          formValues.location == RequestLocationType.HOTEL) ? (
                          <>
                            <span>
                              {moneyFormatter.format(
                                duoDatePartnerTravelDistanceData.costs
                              )}
                            </span>
                            <span className='text-xs font-normal text-muted-foreground'>
                              {`${duoDatePartnerTravelDistanceData.distance} km`}
                            </span>
                          </>
                        ) : (
                          requestPriceOnRequest()
                        )}
                      </div>
                    </>
                  )}

                  <span className='font-bold'>{requestTotalPrice()}</span>
                  <div className='flex flex-col text-end font-bold'>
                    <span>
                      {travelDistanceData == undefined ||
                      totalPrice == undefined
                        ? requestPriceOnRequest()
                        : moneyFormatter.format(
                            travelDistanceData?.costs +
                              totalPrice +
                              (threesome?.price ?? 0) +
                              (duoDatePartnerPrice ?? 0)
                          )}
                    </span>
                  </div>
                </div>

                <Separator className='my-4 w-full'></Separator>

                <FormField
                  control={form.control}
                  name='information'
                  render={({ field }) => (
                    <FormItem className={'w-full'}>
                      <FormLabel>{requestDetailsNoticeTitle()}</FormLabel>
                      <FormDescription>
                        {requestDetailsNoticeDescription()}
                      </FormDescription>
                      <FormControl>
                        <AutosizeTextarea
                          minHeight={100}
                          className=''
                          placeholder={requestDetailsNoticePlaceholder()}
                          maxLength={512}
                          {...field}
                          value={field.value ?? ''}
                          onChange={(val) => {
                            field.onChange(
                              isEmpty(val.currentTarget.value)
                                ? null
                                : val.currentTarget.value
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Separator className='my-4 w-full'></Separator>

                <FormField
                  control={form.control}
                  name='acceptDisclaimer'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow'>
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className='space-y-1 leading-none'>
                        <FormLabel>{requestDetailsDisclaimerYes()}</FormLabel>
                        <FormDescription
                          dangerouslySetInnerHTML={{
                            __html: requestDetailsDisclaimerYesNotice({
                              link: `<a target="_blank" class="underline" href="${
                                baseLocale == language
                                  ? `/agbs`
                                  : `/${language}/terms`
                              }">${requestDetailsDisclaimerYesNoticeTitle()}</a>`,
                            }),
                          }}
                        ></FormDescription>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className='gap-2 p-6 pt-0'>
                <Button
                  onClick={() => {
                    setStep(5);
                  }}
                  type='button'
                  variant={'outline'}
                >
                  {wizardPrev()}
                </Button>
                <SubmitButton disabled={!form.formState.isValid}>
                  {requestSubmit()}
                </SubmitButton>
              </DialogFooter>
            </FormRoot>
          </DialogContent>
        </Dialog>
      </RequestContext.Provider>
    </TooltipProvider>
  );
}
